import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApolloError } from '@apollo/client';

import { useErpVacancyBoardQuery } from 'core/graphql';

import { useStableRefetch } from '../Groups/useStableRefetch';
import { IVacancy } from './careerModel';

// Stable empty fallback — see the note in `useCareerMembers`.
const EMPTY_VACANCIES: IVacancy[] = [];

/**
 * Сколько строк тянем за раз. Совпадает с серверным умолчанием и подобрано под
 * задачу: очередь СЛИВАЮТ, то есть смотрят подряд сверху, а не прыгают по
 * страницам. Двадцать — примерно экран без прокрутки на ноутбуке.
 */
export const VACANCY_PAGE_SIZE = 20;

interface IVacanciesFilters {
  status?: string;
  directionId?: string;
  search?: string;
  createdFrom?: string;
  createdTo?: string;
  freshness?: string;
  hideArchived?: boolean;
  /**
   * ТЗ 0035: фильтруется НА СЕРВЕРЕ. В 0034 это считал клиент — при полном
   * списке безразлично, но под пагинацией клиентский фильтр отсеивал бы внутри
   * страницы, то есть врал бы, а не тормозил.
   */
  source?: string;
  hasDuplicates?: boolean;
  orderBy?: string;
  orderDir?: string;
}

interface IVacanciesHook {
  vacancies: IVacancy[];
  /** Размер ВСЕЙ выборки, не страницы. Иначе «Найдено: 20» на каждой странице
   *  не сообщает модератору ничего. */
  totalCount: number;
  /** Счётчик упёрся в потолок — показывать «1000+», а не «1000». */
  totalCountCapped: boolean;
  hasNextPage: boolean;
  loadMore: () => void;
  loadingMore: boolean;
  loading: boolean;
  error?: ApolloError;
  refetch: () => void;
}

/**
 * Доска вакансий страницами (ТЗ 0035). Курсор, а не смещение: ночной агент
 * вставляет строки в голову сортировки ровно тогда, когда модератор листает
 * очередь, и смещение при вставке в голову показывает одну строку дважды, а
 * другую не показывает вовсе.
 *
 * Страницы КОПЯТСЯ: «показать ещё» дописывает следующие двадцать к уже
 * прочитанным. Так и работает разбор очереди — сверху вниз, с накоплением
 * выделения, а не прыжками между страницами (которых у курсора и нет).
 */
export const useVacancies = (
  filters: IVacanciesFilters = {},
): IVacanciesHook => {
  // Сколько страниц открыто. Это НЕ украшение: любая мутация над строкой
  // обновляет сущность в кэше, запрос с `cache-and-network` перечитывается
  // СВОИМИ переменными — и если там навсегда `first: 20`, то у модератора,
  // открывшего три страницы, после первой же публикации список схлопывается
  // к двадцати строкам, а дровер закрывается посреди разбора. Замерено в
  // браузере: перезапрос идёт и БЕЗ нашей правки кэша, то есть это поведение
  // Apollo, а не она. Поэтому окно запроса растёт вместе с открытым.
  const [pages, setPages] = useState(1);

  // Смена фильтра или сортировки — это новый список, окно начинается заново.
  const filterKey = JSON.stringify(filters);
  useEffect(() => {
    setPages(1);
  }, [filterKey]);

  const {
    data,
    loading,
    error,
    refetch,
    fetchMore,
    networkStatus,
  } = useErpVacancyBoardQuery({
    variables: {
      status: filters.status || undefined,
      directionId: filters.directionId || undefined,
      search: filters.search || undefined,
      createdFrom: filters.createdFrom || undefined,
      createdTo: filters.createdTo || undefined,
      freshness: filters.freshness || undefined,
      hideArchived: filters.hideArchived || undefined,
      source: filters.source || undefined,
      hasDuplicates: filters.hasDuplicates || undefined,
      orderBy: filters.orderBy || undefined,
      orderDir: filters.orderDir || undefined,
      first: VACANCY_PAGE_SIZE * pages,
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  // Pinned identity — this flows into the mutation hook whose callbacks land in
  // the react-table `columns` memo; an unstable one loops the render.
  const stableRefetch = useStableRefetch(refetch);

  const board = data?.careerVacancyBoard;
  const vacancies = useMemo(
    () =>
      board?.edges?.length
        ? (board.edges.map((edge) => edge.node) as IVacancy[])
        : EMPTY_VACANCIES,
    [board],
  );

  const endCursor = board?.pageInfo?.endCursor ?? null;
  const hasNextPage = Boolean(board?.pageInfo?.hasNextPage);

  const loadMore = useCallback(() => {
    if (!hasNextPage || !endCursor) return;
    // Дочитываем РОВНО страницу от курсора, а не `first * pages` заново:
    // курсор здесь затем и нужен, что агент вставляет строки в голову
    // сортировки, пока человек листает. Слияние живёт в политике кэша
    // (`core/cache`), а не здесь: `updateQuery` на каждом вызывающем
    // разъезжается с ней при смене фильтра.
    fetchMore({ variables: { first: VACANCY_PAGE_SIZE, after: endCursor } });
    setPages((prev) => prev + 1);
  }, [fetchMore, hasNextPage, endCursor]);

  return {
    vacancies,
    totalCount: board?.totalCount ?? 0,
    totalCountCapped: Boolean(board?.totalCountCapped),
    hasNextPage,
    loadMore,
    // 3 = fetchMore. Без этого кнопка «показать ещё» не гаснет на время
    // догрузки, и нетерпеливый оператор нажимает её дважды.
    loadingMore: networkStatus === 3,
    loading,
    error,
    refetch: stableRefetch,
  };
};
